import { useNavigate } from "@tanstack/react-router"
import { ChevronDown, ShieldCheck } from "lucide-react"
import { useState } from "react"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@holigral/shared/ui/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@holigral/shared/ui/components/ui/dropdown-menu"
import { useIsRouteActive } from "@holigral/shared/routing"
import {
  useEffectiveTenantSlug,
  useGetTenantsQuery,
  useUpdateSettingsMutation,
} from "@/tenant"
import { useIsAdmin } from "@/auth"
import { useT } from "@/i18n"

export default function TenantSelect() {
  const { t } = useT("common")
  const tenantSlug = useEffectiveTenantSlug()
  const navigate = useNavigate()
  const { data: tenants } = useGetTenantsQuery()
  const [updateSettings] = useUpdateSettingsMutation()
  const [open, setOpen] = useState(false)
  const isAdmin = useIsAdmin()
  // In Admin mode the switcher stays visible but reads "Admin" (product.md
  // §9.1): no workspace is selected, and picking one is how you leave.
  const isAdminArea = useIsRouteActive({ to: "/admin", fuzzy: true })

  // Resolve the name from the header-free tenants list so the switcher works
  // on slug-less pages (e.g. /myprofiles), where /tenants/current cannot.
  const currentName = tenants?.find(({ slug }) => slug === tenantSlug)?.name

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger
            render={
              <SidebarMenuButton>
                <span className="group-data-[state=collapsed]:hidden">
                  {isAdminArea ? t("nav.admin") : currentName}
                </span>
                <ChevronDown className="ml-auto" />
              </SidebarMenuButton>
            }
          />
          <DropdownMenuContent>
            {isAdmin && (
              <>
                {/* Pinned for platform admins (product.md §10.2) */}
                <DropdownMenuItem
                  onClick={async () => {
                    setOpen(false)
                    await navigate({ to: "/admin" })
                  }}
                >
                  <ShieldCheck />
                  {t("nav.admin")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuRadioGroup
              value={isAdminArea ? "" : tenantSlug}
              onValueChange={async (slug: string) => {
                setOpen(false)
                await navigate({
                  to: "/t/$tenantSlug/dashboard",
                  params: { tenantSlug: slug },
                })
                await updateSettings({
                  currentTenantSlug: slug,
                })
              }}
            >
              {tenants?.map(({ slug, name }) => (
                <DropdownMenuRadioItem key={slug} value={slug}>
                  {name}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await navigate({ to: "/onboarding" })
              }}
            >
              {t("nav.newWorkspace")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
